import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Send, Bot, User, Loader2, Trash2, Sparkles, ImageIcon, FileText, Lightbulb, PenTool, BookOpen, Rocket } from 'lucide-react';
import { toast } from 'sonner';

const QUICK_ACTIONS = [
  { label: 'Create Digital Product', icon: Sparkles, prompt: 'I want to create a digital product', redirect: '/ai-product-creator' },
  { label: 'Generate Ideas', icon: Lightbulb, prompt: 'Give me 5 creative digital product ideas I could create and sell.' },
  { label: 'Write Description', icon: FileText, prompt: 'Write a compelling product description for a digital product called ' },
  { label: 'Pricing Strategy', icon: PenTool, prompt: 'Help me price my digital product. It\'s a ' },
];

export default function AssistantPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: `Hello! I'm the Susan David AI Creator Assistant.

I help you:
• Create digital products (ebooks, guides, planners, toolkits & more)
• Write marketplace descriptions
• Generate cover ideas and mockup directions
• Suggest pricing strategies
• Prepare social media captions
• Publish products faster inside the marketplace

Tell me what you'd like to create today!` }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `assistant-${user?.id}-${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/assistant/chat', { message: msg, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      
      // Show redirect suggestion if backend detected product creation intent
      if (res.data.redirect_suggestion) {
        toast.info(
          <div className="flex items-center gap-2">
            <Rocket size={16} />
            <span>Open AI Product Creator?</span>
            <Button 
              size="sm" 
              className="h-7 text-xs ml-2"
              onClick={() => navigate(res.data.redirect_suggestion)}
            >
              Go
            </Button>
          </div>,
          { duration: 8000 }
        );
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
      toast.error('Request failed');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleQuickAction = (action) => {
    // Direct redirect for product creator
    if (action.redirect) {
      navigate(action.redirect);
      return;
    }
    if (action.prompt.endsWith(' ')) {
      setInput(action.prompt);
      inputRef.current?.focus();
    } else {
      sendMessage(action.prompt);
    }
  };

  const clearChat = () => {
    setMessages([{ role: 'assistant', content: 'Chat cleared. How can I help you create today?' }]);
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="max-w-2xl mx-auto animate-in flex flex-col h-[calc(100vh-7rem)]" data-testid="assistant-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
            <Bot size={18} className="text-gold" />
          </div>
          <div>
            <h1 className="font-serif text-2xl md:text-3xl">AI Assistant</h1>
            <p className="text-sm text-muted-foreground">Content creation and community guide</p>
          </div>
        </div>
        <Button data-testid="clear-chat-btn" variant="outline" size="sm" className="rounded-sm text-xs" onClick={clearChat}>
          <Trash2 size={12} className="mr-1" /> Clear
        </Button>
      </div>

      {/* Quick Actions */}
      {messages.length <= 1 && (
        <div className="grid grid-cols-2 gap-2 mb-4" data-testid="quick-actions">
          {QUICK_ACTIONS.map((a, i) => (
            <button
              key={i}
              data-testid={`quick-action-${i}`}
              onClick={() => handleQuickAction(a)}
              className="flex items-center gap-2 p-3 border border-border/40 hover:border-gold/40 hover:bg-gold/5 transition-all text-left group"
            >
              <a.icon size={16} className="text-muted-foreground group-hover:text-gold transition-colors shrink-0" />
              <span className="text-xs font-medium">{a.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Chat Messages */}
      <div className="flex-1 border border-border/40 overflow-hidden flex flex-col">
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-msg-${i}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot size={14} className="text-gold" />
                  </div>
                )}
                <div className={`max-w-[80%] group relative ${
                  msg.role === 'user'
                    ? 'bg-obsidian text-white px-4 py-3'
                    : 'bg-mist text-foreground border border-border/20 px-4 py-3'
                }`}>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</div>
                  {msg.role === 'assistant' && i > 0 && (
                    <button
                      onClick={() => copyMessage(msg.content)}
                      className="absolute -bottom-6 left-0 text-[10px] text-muted-foreground hover:text-gold opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Copy
                    </button>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-obsidian flex items-center justify-center shrink-0 mt-0.5">
                    <User size={14} className="text-white" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3" data-testid="assistant-typing">
                <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                  <Bot size={14} className="text-gold" />
                </div>
                <div className="bg-mist border border-border/20 px-4 py-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={scrollRef} />
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="border-t border-border/40 p-3 flex gap-2">
          <Input
            ref={inputRef}
            data-testid="assistant-input"
            placeholder="Ask me to create content, generate ideas, or anything..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            disabled={loading}
            className="rounded-sm text-sm h-10"
          />
          <Button
            data-testid="assistant-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="rounded-sm h-10 px-4 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300"
          >
            <Send size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
